import { useState } from 'react';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { toaster } from '@/components/ui/toaster';
import { useAdminStripe } from './useAdminStripe';
import type { PriceItem } from '@/api/stripeService';

const QUARTERLY_NONE = '__none__';

const intervalLabel = (interval: string) => {
  if (interval === 'year') return 'Annuel';
  if (interval === 'month') return 'Mensuel';
  return interval;
};

const formatEur = (amount: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(amount);

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const priceLabel = (item: PriceItem) => `${formatCurrency(item.amount, item.currency)} — ${item.price_id}`;

const StripePage = () => {
  const {
    plans,
    plansLoading,
    products,
    productsLoading,
    activePriceConfig,
    setActivePrice,
    isSettingActive,
  } = useAdminStripe();

  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedMonthlyPriceId, setSelectedMonthlyPriceId] = useState('');
  const [selectedYearlyPriceId, setSelectedYearlyPriceId] = useState('');
  const [selectedQuarterlyPriceId, setSelectedQuarterlyPriceId] = useState(QUARTERLY_NONE);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const selectedProduct = products.find((p) => p.product_id === selectedProductId) ?? null;

  const handleProductChange = (productId: string) => {
    setSelectedProductId(productId);
    const product = products.find((p) => p.product_id === productId);
    setSelectedMonthlyPriceId(product?.monthly_prices[0]?.price_id ?? '');
    setSelectedYearlyPriceId(product?.yearly_prices[0]?.price_id ?? '');
    setSelectedQuarterlyPriceId(
      product?.quarterly_prices.length ? product.quarterly_prices[0].price_id : QUARTERLY_NONE
    );
  };

  const handleSetActiveClick = () => {
    if (!selectedProductId || !selectedMonthlyPriceId || !selectedYearlyPriceId) {
      toaster.create({
        title: 'Sélection incomplète',
        description: 'Choisissez un produit, un prix mensuel et un prix annuel',
        type: 'warning',
      });
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    try {
      await setActivePrice({
        monthlyPriceId: selectedMonthlyPriceId,
        yearlyPriceId: selectedYearlyPriceId,
        quarterlyPriceId: selectedQuarterlyPriceId === QUARTERLY_NONE ? null : selectedQuarterlyPriceId,
      });
      setConfirmOpen(false);
    } catch {
      // error toast handled in the mutation
    }
  };

  const selectedMonthly = selectedProduct?.monthly_prices.find((p) => p.price_id === selectedMonthlyPriceId);
  const selectedYearly = selectedProduct?.yearly_prices.find((p) => p.price_id === selectedYearlyPriceId);
  const selectedQuarterly = selectedProduct?.quarterly_prices.find(
    (p) => p.price_id === selectedQuarterlyPriceId
  );

  return (
    <>
      <PageHeader title="Stripe" description="Répartition des plans et configuration du prix actif" />

      <div className="flex flex-col gap-6">
        <Card className="p-0">
          <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-800">
            <p className="text-base font-semibold text-gray-800 dark:text-gray-100">Plan Breakdown</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Abonnés actifs et contribution au MRR par plan de facturation
            </p>
          </div>

          {plansLoading ? (
            <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Chargement…</p>
          ) : plans.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Aucune donnée de plan disponible
            </p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>Plan</TH>
                  <TH>Interval</TH>
                  <TH className="text-right">Prix</TH>
                  <TH className="text-right">Abonnés</TH>
                  <TH className="text-right">MRR</TH>
                </tr>
              </THead>
              <TBody>
                {plans.map((plan) => (
                  <TR key={plan.planId}>
                    <TD className="font-medium text-gray-900 dark:text-gray-100">{plan.planName}</TD>
                    <TD>{intervalLabel(plan.interval)}</TD>
                    <TD className="text-right">{formatEur(plan.price)}</TD>
                    <TD className="text-right">{plan.activeSubs}</TD>
                    <TD className="text-right">{formatEur(plan.mrr)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>

        <Card>
          <div className="mb-6">
            <p className="text-base font-semibold text-gray-800 dark:text-gray-100">
              Select Active Price Configuration
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Choisissez la configuration de prix Stripe utilisée pour les nouveaux abonnements
            </p>
          </div>

          {productsLoading ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Chargement des produits depuis Stripe…
            </p>
          ) : products.length === 0 ? (
            <div className="rounded-lg border border-warning-500/40 bg-warning-500/10 px-4 py-3 text-sm text-warning-500">
              Aucun produit avec prix mensuel et annuel trouvé dans Stripe.
            </div>
          ) : (
            <>
              <div className="mb-6 rounded-lg border border-brand-500/30 bg-brand-50 px-4 py-3 dark:bg-brand-500/10">
                <p className="text-sm font-semibold text-brand-700 dark:text-brand-300">
                  Configuration active actuelle
                </p>
                {activePriceConfig ? (
                  <div className="mt-1 text-sm text-gray-700 dark:text-gray-200">
                    <p className="font-medium">{activePriceConfig.product_name}</p>
                    <p>
                      Mensuel : {formatCurrency(activePriceConfig.monthly.amount, activePriceConfig.monthly.currency)}{' '}
                      | Annuel : {formatCurrency(activePriceConfig.yearly.amount, activePriceConfig.yearly.currency)}
                      {activePriceConfig.quarterly && (
                        <>
                          {' '}
                          | Trimestriel :{' '}
                          {formatCurrency(activePriceConfig.quarterly.amount, activePriceConfig.quarterly.currency)}
                        </>
                      )}
                    </p>
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Aucune configuration active trouvée
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-4">
                <Select
                  label="Produit"
                  value={selectedProductId}
                  onChange={(e) => handleProductChange(e.target.value)}
                >
                  <option value="">— Sélectionner un produit —</option>
                  {products.map((p) => (
                    <option key={p.product_id} value={p.product_id}>
                      {p.product_name}
                      {activePriceConfig?.product_id === p.product_id ? ' (ACTIF)' : ''}
                    </option>
                  ))}
                </Select>

                <Select
                  label="Prix mensuel"
                  value={selectedMonthlyPriceId}
                  onChange={(e) => setSelectedMonthlyPriceId(e.target.value)}
                  disabled={!selectedProductId}
                >
                  {!selectedProductId && <option value="">— Sélectionnez d'abord un produit —</option>}
                  {selectedProduct?.monthly_prices.map((p) => (
                    <option key={p.price_id} value={p.price_id}>
                      {priceLabel(p)}
                      {activePriceConfig?.monthly.price_id === p.price_id ? ' (ACTIF)' : ''}
                    </option>
                  ))}
                </Select>

                <Select
                  label="Prix annuel"
                  value={selectedYearlyPriceId}
                  onChange={(e) => setSelectedYearlyPriceId(e.target.value)}
                  disabled={!selectedProductId}
                >
                  {!selectedProductId && <option value="">— Sélectionnez d'abord un produit —</option>}
                  {selectedProduct?.yearly_prices.map((p) => (
                    <option key={p.price_id} value={p.price_id}>
                      {priceLabel(p)}
                      {activePriceConfig?.yearly.price_id === p.price_id ? ' (ACTIF)' : ''}
                    </option>
                  ))}
                </Select>

                <Select
                  label="Prix trimestriel"
                  value={selectedQuarterlyPriceId}
                  onChange={(e) => setSelectedQuarterlyPriceId(e.target.value)}
                  disabled={!selectedProductId}
                >
                  <option value={QUARTERLY_NONE}>Aucun / Désactiver</option>
                  {selectedProduct?.quarterly_prices.map((p) => (
                    <option key={p.price_id} value={p.price_id}>
                      {priceLabel(p)}
                      {activePriceConfig?.quarterly?.price_id === p.price_id ? ' (ACTIF)' : ''}
                    </option>
                  ))}
                </Select>

                <div>
                  <Button
                    onClick={handleSetActiveClick}
                    disabled={!selectedProductId || !selectedMonthlyPriceId || !selectedYearlyPriceId}
                  >
                    Définir comme prix actif
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirmer le changement de configuration de prix"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={isSettingActive}>
              Annuler
            </Button>
            <Button onClick={handleConfirm} disabled={isSettingActive}>
              {isSettingActive ? 'Enregistrement…' : 'Confirmer'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 text-sm text-gray-700 dark:text-gray-200">
          <p>
            Vous êtes sur le point de définir la configuration de prix suivante comme active pour les
            nouveaux abonnements :
          </p>
          <div className="flex flex-col gap-2 rounded-lg bg-gray-50 p-4 dark:bg-gray-800/60">
            <div className="flex justify-between">
              <span className="font-semibold">Produit :</span>
              <span>{selectedProduct?.product_name}</span>
            </div>
            {selectedMonthly && (
              <div className="flex justify-between">
                <span className="font-semibold">Mensuel :</span>
                <span>{formatCurrency(selectedMonthly.amount, selectedMonthly.currency)}</span>
              </div>
            )}
            {selectedYearly && (
              <div className="flex justify-between">
                <span className="font-semibold">Annuel :</span>
                <span>{formatCurrency(selectedYearly.amount, selectedYearly.currency)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="font-semibold">Trimestriel :</span>
              {selectedQuarterly ? (
                <span>{formatCurrency(selectedQuarterly.amount, selectedQuarterly.currency)}</span>
              ) : (
                <span className="text-gray-400">Désactivé</span>
              )}
            </div>
            <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
              <p className="font-mono text-xs text-gray-500 dark:text-gray-400">
                Monthly ID : {selectedMonthlyPriceId}
              </p>
              <p className="font-mono text-xs text-gray-500 dark:text-gray-400">
                Yearly ID : {selectedYearlyPriceId}
              </p>
              {selectedQuarterlyPriceId !== QUARTERLY_NONE && (
                <p className="font-mono text-xs text-gray-500 dark:text-gray-400">
                  Quarterly ID : {selectedQuarterlyPriceId}
                </p>
              )}
            </div>
          </div>
          <div className="rounded-lg border border-brand-500/30 bg-brand-50 px-4 py-3 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
            Ceci met à jour les prix utilisés pour les nouveaux abonnements. Les abonnements existants
            ne sont pas affectés.
          </div>
        </div>
      </Modal>
    </>
  );
};

export default StripePage;
